import { toLocalDateInput } from "../../../services/dateTime";

export const emptyBatchValues = {
  raw_material: "",
  input_ready_batch_id: "",
  item_id: "",
  batch_quantity: "",
  target_delivery_at: "",
  sap_order_number: "",
  trial_mode: false,
};

export const changeBatchField = (values, name, value) => ({
  ...values,
  [name]: value,
  ...(["raw_material", "input_ready_batch_id"].includes(name)
    ? { item_id: "", batch_quantity: "", cycle_time: null } : {}),
  ...(name === "raw_material" ? { input_ready_batch_id: "" } : {}),
});

export const resolveBatchInput = (values, inputRecords, partOptions, materialOptions = []) => {
  const material = materialOptions.find((option) => String(option.value) === String(values.raw_material));
  const resolvedValues = {
    ...values,
    sap_order_number: material?.raw_material_code ?? material?.raw?.raw_material_code ?? "",
  };
  if (!values.input_ready_batch_id) return resolvedValues;
  const input = inputRecords.find((row) => row.input_ready_batch_id === values.input_ready_batch_id
    && row.raw_material_id === values.raw_material);
  const part = input && partOptions.find((option) => input.item_id
    ? option.value === input.item_id : input.item_code && option.raw?.item_code === input.item_code);
  return {
    ...resolvedValues,
    item_id: part?.value || "",
    batch_quantity: input?.quantity ?? "",
    cycle_time: null,
  };
};

export const formatProductionDuration = (seconds) => {
  if (seconds == null || !Number.isFinite(Number(seconds)) || Number(seconds) < 0) return "";
  const total = Math.round(Number(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainder = total % 60;
  return [hours && `${hours}h`, minutes && `${minutes}m`, (remainder || !total) && `${remainder}s`]
    .filter(Boolean).join(" ");
};

export const getBatchTiming = (values, partOptions) => {
  const part = partOptions.find((option) => String(option.value) === String(values.item_id));
  const rawCycle = part?.production_cycle_time ?? part?.raw?.production_cycle_time ?? values.cycle_time;
  const cycleTime = rawCycle == null || rawCycle === "" ? null : Number(rawCycle);
  const quantity = Number(values.batch_quantity);
  const duration = Number.isFinite(cycleTime) && cycleTime > 0 && Number.isFinite(quantity) && quantity > 0
    ? cycleTime * quantity
    : null;
  return { cycleTime, duration };
};

export const validateBatchValues = (values, partOptions, materialOptions, inputRecords = [], existingBatch = null) => {
  const errors = {};
  const legacyStarted = existingBatch && !existingBatch.input_ready_batch_id
    && (existingBatch.started_at || existingBatch.cycles?.length);
  const input = inputRecords.find((row) => row.input_ready_batch_id === values.input_ready_batch_id
    && row.raw_material_id === values.raw_material);
  if (!legacyStarted) {
    if (!input) errors.input_ready_batch_id = "Select an input batch for this raw material.";
    else if (input.is_processed && existingBatch?.input_ready_batch_id !== input.input_ready_batch_id) {
      errors.input_ready_batch_id = "This input batch has already been processed.";
    }
  }
  if (!values.raw_material) errors.raw_material = "Select a raw material.";
  else if (!materialOptions.some((option) => String(option.value) === String(values.raw_material))) {
    errors.raw_material = "Select a raw material from this plant.";
  }
  if (!values.item_id) errors.item_id = "Link a valid finished item to the input batch first.";
  else if (!partOptions.some((option) => String(option.value) === String(values.item_id))) {
    errors.item_id = "The linked item must belong to this plant.";
  }
  else if (!(getBatchTiming(values, partOptions).duration > 0) && Number(values.batch_quantity) > 0) {
    errors.item_id = "Configure a valid cycle time for the selected part.";
  }
  const quantity = Number(values.batch_quantity);
  if (!Number.isSafeInteger(quantity) || quantity <= 0) {
    errors.batch_quantity = "Enter a whole number greater than zero.";
  }
  if (values.target_delivery_at) {
    const date = new Date(`${values.target_delivery_at}T00:00:00`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(values.target_delivery_at) ||
      Number.isNaN(date.getTime()) || date.getDate() !== Number(values.target_delivery_at.slice(-2))) {
      errors.target_delivery_at = "Enter a valid target date.";
    }
  }
  return errors;
};

export const buildBatchPayload = (values, partOptions) => {
  const { duration } = getBatchTiming(values, partOptions);
  return {
    raw_material: values.raw_material,
    ...(values.input_ready_batch_id ? { input_ready_batch_id: values.input_ready_batch_id } : {}),
    item_id: values.item_id,
    batch_quantity: Number(values.batch_quantity),
    production_duration: duration,
    target_delivery_at: values.target_delivery_at
      ? new Date(`${values.target_delivery_at}T00:00:00`).toISOString()
      : null,
    sap_order_number: values.sap_order_number?.trim() || null,
    trial_mode: Boolean(values.trial_mode),
  };
};

export const toBatchFormValues = (batch) => ({
  ...emptyBatchValues,
  raw_material: batch?.raw_material ?? "",
  input_ready_batch_id: batch?.input_ready_batch_id ?? "",
  item_id: batch?.item_id ?? batch?.part_number ?? "",
  batch_quantity: batch?.batch_quantity ?? batch?.quantity ?? "",
  cycle_time: batch?.cycle_time ?? (
    batch?.production_duration && batch?.batch_quantity
      ? batch.production_duration / batch.batch_quantity
      : null
  ),
  target_delivery_at: toLocalDateInput(batch?.target_delivery_at ?? batch?.target_date),
  sap_order_number: batch?.sap_order_number ?? batch?.sap_production_order_number ?? "",
  trial_mode: Boolean(batch?.trial_mode ?? batch?.trial_batch),
});

export const unwrapBatch = (response) => {
  const batch = Array.isArray(response?.data) ? response.data[0] : response?.data ?? response;
  if (response?.status === "failed" || !batch || !(batch.id || batch.uid || batch.batch_id)) {
    throw new Error(response?.message || "The batch could not be saved. Please try again.");
  }
  return batch;
};

export const printBatchQr = (qrElement, batch) => {
  if (!qrElement) throw new Error("The QR code is not ready to print.");
  const frame = document.createElement("iframe");
  frame.title = "Batch QR print preview";
  frame.style.cssText = "position:fixed;width:0;height:0;border:0;";
  document.body.appendChild(frame);
  const printDocument = frame.contentDocument;
  const printWindow = frame.contentWindow;
  if (!printDocument || !printWindow) {
    frame.remove();
    throw new Error("Unable to open the print preview.");
  }
  printDocument.title = `Batch ${batch.batch_id}`;
  const style = printDocument.createElement("style");
  style.textContent = "@page{size:auto;margin:12mm}body{font:14px sans-serif;text-align:center}svg{width:50mm;height:50mm}h1{font-size:18px}p{margin:8px 0}";
  printDocument.head.appendChild(style);
  const title = printDocument.createElement("h1");
  title.textContent = `Batch ${batch.batch_id}`;
  printDocument.body.appendChild(title);
  printDocument.body.appendChild(qrElement.cloneNode(true));
  const details = printDocument.createElement("p");
  details.textContent = `Part: ${batch.item_id || "—"} · Quantity: ${batch.batch_quantity ?? "—"}`;
  printDocument.body.appendChild(details);
  printWindow.onafterprint = () => frame.remove();
  window.setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 50);
  window.setTimeout(() => frame.remove(), 60000);
};
